import { DecreeMultipleSelect } from "@/components/form-control/DecreeMultipleSelect";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DecreeCategory, ParkingAngle } from "@/types/gql/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { DropdownSelect } from "components/form-control";
import { Feature, LineString } from "geojson";
import { calculateArea } from "helper/format-data";
import { isNil } from "helper/utils";
import * as React from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CurbZone } from "types/curb-zone.type";
import { z } from "zod";
import { CurbZonePropertiesPromptProps } from "./curb-zone-prompt.type";
import { curbZoneFormSchema, parkingAngleOptions } from "./curb-zone.const";
import { PolicyView } from "./policy-view";

export const CurbZonePropertiesPrompt = ({
  registerProperties,
  prefill,
  cancel,
  id,
}: CurbZonePropertiesPromptProps) => {
  const [curbPolicyIds, setCurbPolicyIds] = useState<string[]>([]);
  const selectedDecrees =
    prefill?.properties?.decrees?.map((decree) => ({
      value: decree.id,
      label: decree.name,
    })) ?? [];
  const form = useForm({
    resolver: zodResolver(curbZoneFormSchema),
    defaultValues: {
      ...prefill?.properties,
      parkingAngle: prefill?.properties.parkingAngle ?? ParkingAngle.Parallel,
      externalId: prefill?.properties.externalId ?? undefined,
      note: prefill?.properties.note ?? undefined,
      curbPolicyIds: prefill?.properties.curbPolicyIds ?? [],
      selectedDecrees,
      streetName: prefill?.properties.streetName ?? undefined,
      streetNumberStart: prefill?.properties.streetNumberStart ?? undefined,
      streetNumberEnd: prefill?.properties.streetNumberEnd ?? undefined,
      length: prefill?.properties.length ?? undefined,
      width: prefill?.properties.width ?? undefined,
    },
    mode: "all",
  });

  useEffect(() => {
    if (prefill?.properties?.curbPolicies) {
      const currentCurbPolicies = prefill.properties.curbPolicies.map((item) => item.curbPolicyId) as string[];
      setCurbPolicyIds(currentCurbPolicies);
    }
  }, [prefill]);

  const isEdit = !isNil(id);
  const dismiss = () => cancel(isEdit);
  const saveParking = (promptedProperties: z.infer<typeof curbZoneFormSchema>) => {
    const newParkingProperty = {
      ...promptedProperties,
      curbPolicyIds,
      decreeIds: promptedProperties.selectedDecrees?.map((decree) => decree.value),
    } as CurbZone;
    registerProperties(newParkingProperty);
  };

  const calculateCurbZoneProperties = (value: string) => {
    const curbZoneFeature = {
      ...prefill,
      properties: { ...prefill?.properties, width: Number(value) },
    } as Feature<LineString, CurbZone>;
    const { calculatedArea, calculatedSpaces } = calculateArea(curbZoneFeature);

    form.setValue("calculatedArea", calculatedArea);
    form.setValue("calculatedSpaces", calculatedSpaces);
  };

  return (
    <Dialog open>
      <DialogContent className="w-xl h-[calc(100vh-5em)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{`${isEdit ? "Edit" : "Add"} Curb zone`}</DialogTitle>
          <DialogDescription>Curb zone informations</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(saveParking)} className="space-y-6">
            <PolicyView curbPolicies={curbPolicyIds} setCurbPolicies={setCurbPolicyIds} />

            <FormField
              control={form.control}
              name="parkingAngle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Forme</FormLabel>
                  <FormControl>
                    <DropdownSelect
                      options={parkingAngleOptions}
                      value={field.value ?? ParkingAngle.Parallel}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="streetName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nom voie<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="streetNumberStart"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Début du numéro de rue</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="streetNumberEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fin du numéro de rue</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="length"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Longueur</FormLabel>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="width"
              render={({ field: { onChange, ...agr } }) => (
                <FormItem>
                  <FormLabel>Largeur</FormLabel>
                  <FormControl>
                    <Input
                      {...agr}
                      type="number"
                      onChange={(e) => {
                        onChange(Number(e.target.value));
                        calculateCurbZoneProperties(e.target.value);
                      }}
                      min={0}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="calculatedArea"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Surface calculée</FormLabel>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="calculatedSpaces"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Places calculées<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input value={value} onChange={(e) => onChange(Number(e.target.value))} type="number" min={1} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DecreeMultipleSelect categories={[DecreeCategory.Permanent]} />

            <FormField
              control={form.control}
              name="externalId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Identifiant externe</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Commentaire</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="secondary" onClick={dismiss}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
